<template>
  <div class="toast-message" :class="`toast-message--${type}`">
    <div class="toast-badge" :class="`toast-badge--${type}`">
      <BackstageIcon :name="getIcon()!" />
    </div>
    <div class="toast-content">
      <p class="toast-content__title">{{ title }}</p>
      <p class="toast-content__message">{{ message }}</p>
    </div>
    <BackstageIcon :name="Icons.CROSS" class="toast-close" @click="$emit('close')" />
    <div class="toast-progress-bar"></div>
  </div>
</template>

<script setup lang="ts">
import { Icons } from '@/models/icons.enum';
import { computed } from 'vue';
import { MessageType } from '../../plugins/toast/enums';
import BackstageIcon from './BackstageIcon.vue';

interface Props {
  title?: string;
  message: string;
  type: MessageType;
  icon?: Icons;
  duration: number;
}
const props = defineProps<Props>();

const animationDuration = computed(() => `${props.duration}ms`);
const progressBarColor = computed(() =>
  props.type === MessageType.INFO ? 'var(--content-system-color)' : 'var(--content-positive-color)',
);

const getIcon = () => {
  if (props.icon) return props.icon;

  switch (props.type) {
    case MessageType.INFO:
      return Icons.BLUE_INFO;
    case MessageType.SUCCESS:
      return Icons.GREEN_GLITTER;
  }
};
</script>

<style lang="scss" scoped>
.toast-message {
  display: flex;
  align-items: flex-start;
  column-gap: 16px;
  background-color: white;
  color: var(--primary-text-color);
  padding: 16px 24px;
  border-radius: 2px;
  box-shadow:
    0px 0px 4px 0px rgba(0, 0, 0, 0.05),
    0px 16px 32px 0px rgba(0, 0, 0, 0.1);
  position: relative;
  min-width: 320px;
  max-width: 560px;
  margin-bottom: 12px;
  overflow: hidden;

  &--info {
    background-color: var(--surface-system-informative-color);
  }

  &--success {
    background-color: var(--surface-system-success-color);
  }
}

.toast-badge {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  margin-top: 2px;
  display: flex;
  align-items: center;
  justify-content: center;

  &--info {
    background-color: var(--content-system-color);

    :deep(i) {
      background-color: white;
    }
  }

  &--success {
    background-color: var(--content-positive-color);

    :deep(i) {
      background-color: white;
    }
  }
}

.toast-content {
  flex: 1;
  min-width: 0;
  text-align: left;

  &__title,
  &__message {
    margin: 0;
    line-height: 1.5;
  }

  &__title {
    font-weight: 700;
  }

  &__message {
    font-weight: 400;
  }
}

.toast-close {
  flex-shrink: 0;
  cursor: pointer;
  background-color: var(--primary-text-color);
}

.toast-progress-bar {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 4px;
  background-color: v-bind(progressBarColor);
  animation-name: progress-shrink;
  animation-timing-function: linear;
  animation-fill-mode: forwards;
  animation-duration: v-bind(animationDuration);
}

@keyframes progress-shrink {
  from {
    width: 100%;
  }

  to {
    width: 0%;
  }
}
</style>
